import type { CvssAssessment } from '@features/applications/applications.models';

import type { CvssRating } from './severity.const';

export type ScoreType = 'v2' | 'v3' | 'v4';

type CvssRatingSource = Pick<
  CvssAssessment,
  'v2Score' | 'v2Severity' | 'v3Score' | 'v3Severity' | 'v4Score' | 'v4Severity'
>;

/**
 * Returns the primary CVSS assessment, or the first one if none is primary.
 */
export const getPrimaryCvss = <T extends { isPrimary?: boolean }>(
  assessments?: T[],
): T | undefined => {
  if (!assessments?.length) return undefined;
  return assessments.find(a => a.isPrimary) ?? assessments[0];
};

/**
 * Maps a CVSS v3.x/v4.0 numeric score to the qualitative rating defined by
 * that scale. v2.0 uses its own, narrower table below - not valid for a v2
 * score.
 *
 * 0.0 → none, 0.1-3.9 → low, 4.0-6.9 → medium, 7.0-8.9 → high,
 * 9.0-10.0 → critical.
 */
const getV3RatingFromCvssScore = (
  score: number | null | undefined,
): CvssRating => {
  if (score == null) return 'unknown';
  if (score >= 9) return 'critical';
  if (score >= 7) return 'high';
  if (score >= 4) return 'medium';
  if (score > 0) return 'low';
  return 'none';
};

/**
 * Maps a CVSS v2.0 numeric score to its own rating table
 * (https://www.first.org/cvss/v2/guide) - narrower than v3.x/v4.0's: no
 * `none` or `critical` tier exists for v2.
 */
const getV2RatingFromCvssScore = (
  score: number | null | undefined,
): CvssRating => {
  if (score == null) return 'unknown';
  if (score >= 7) return 'high';
  if (score >= 4) return 'medium';
  return 'low';
};

/**
 * Resolves the qualitative rating for one CVSS version of an assessment,
 * preferring the assessment's own rating over banding its raw score.
 *
 * Dot, label and chip must all read this single resolved rating: if the
 * assessment's rating ever disagrees with banding its score, label and chip
 * follow the rating and may not bracket the printed score.
 */
export const getCvssRating = (
  assessment: CvssRatingSource | null | undefined,
  scoreType: ScoreType,
): CvssRating => {
  const rating = assessment?.[`${scoreType}Severity`];
  if (rating) return rating;

  const score = assessment?.[`${scoreType}Score`];
  return scoreType === 'v2'
    ? getV2RatingFromCvssScore(score)
    : getV3RatingFromCvssScore(score);
};
